import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import { Button, Modal } from 'antd';
import * as Yup from 'yup';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { editTeammate } from 'redux/reducers/recruiterSlice';
import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { getFullName } from 'utils/personUtils';
import {
  TeammateModel,
  TeammateRoleEnum,
  UpdateTeammateModel,
} from 'api/backofficeApiClient';
import { normalCharactersLength } from 'constants/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getTeammateRoleOptions } from 'utils/teammateUtils';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  teammateDetails?: TeammateModel;
};

interface TeammateModelValues {
  teammateName: string;
  email: string;
  role?: TeammateRoleEnum;
}

const validationSchema = Yup.object({
  teammateName: Yup.string().max(
    normalCharactersLength,
    `Teammate name should be ${normalCharactersLength} characters maximum`,
  ),
  email: Yup.string()
    .email('Email format is invalid')
    .max(
      normalCharactersLength,
      `Email should be ${normalCharactersLength} characters maximum`,
    ),
  role: Yup.number(),
});

export default function EditTeammateModal(props: Props) {
  const { onClose, teammateDetails, onSuccess } = props;
  const dispatch = useAppDispatch();

  const formRef: any = useRef();

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const initialValues: TeammateModelValues = {
    teammateName: getFullName(
      teammateDetails?.firstName,
      teammateDetails?.lastName,
    ),
    email: teammateDetails?.email || '',
    role: teammateDetails?.role,
  };

  const handleSubmit = async (values: TeammateModelValues) => {
    const teammate: UpdateTeammateModel = {
      userId: teammateDetails?.userId,
      role: values.role,
    };

    const result = await dispatch(editTeammate(teammate));

    if (editTeammate.fulfilled.match(result)) {
      onClose();
      onSuccess();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title="Edit Teammate"
      onCancel={handleModalCancel}
      maxWidth={468}
      open
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <FormItem name="teammateName" label="Teammate Name">
              <Input name="teammateName" disabled={true} autoComplete="off" />
            </FormItem>
            <FormItem name="email" label="Email">
              <Input name="email" disabled={true} autoComplete="off" />
            </FormItem>
            <FormItem name="role" label="Role">
              <Select name="role">
                {getTeammateRoleOptions.map((option) => (
                  <Select.Option
                    key={`select-teammate-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          </div>
          <BaseModalFooter>
            <div>
              <Button onClick={handleModalCancel}>Cancel</Button>
              <SubmitButton>Save</SubmitButton>
            </div>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
